import React, { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Landing from './pages/Landing';

// Route-level splitting keeps dashboard libraries (charts, PDF and QR tools) out
// of the public landing-page bundle until a visitor actually needs them.
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Cards = lazy(() => import('./pages/Cards'));
const Customers = lazy(() => import('./pages/Customers'));
const Campaigns = lazy(() => import('./pages/Campaigns'));
const AIEngine = lazy(() => import('./pages/AIEngine'));
const Cashier = lazy(() => import('./pages/Cashier'));
const CardBuilder = lazy(() => import('./pages/CardBuilder'));
const CustomerPortal = lazy(() => import('./pages/CustomerPortal'));
const Settings = lazy(() => import('./pages/Settings'));
const Reports = lazy(() => import('./pages/Reports'));
const Employees = lazy(() => import('./pages/Employees'));
const Branches = lazy(() => import('./pages/Branches'));
const Billing = lazy(() => import('./pages/Billing'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const LegalPage = lazy(() => import('./pages/LegalPage'));

function Private({ children, allowInactiveSubscription = false }) {
  const location = useLocation();
  const token = localStorage.getItem('token');
  const [state, setState] = useState({
    checking: Boolean(token && !allowInactiveSubscription),
    allowed: true,
  });

  useEffect(() => {
    if (!token || allowInactiveSubscription) return undefined;

    let mounted = true;
    setState({ checking: true, allowed: true });
    import('./api')
      .then(({ getBillingSnapshot }) => getBillingSnapshot())
      .then((response) => {
        if (!mounted) return;
        const isValid = Boolean(response.data?.subscription?.is_valid);
        setState({ checking: false, allowed: isValid });
        if (!isValid) {
          localStorage.setItem('subscriptionGate', JSON.stringify({
            code: 'SUBSCRIPTION_REQUIRED',
            redirect_to: '/billing',
            subscription: response.data?.subscription || null,
          }));
        }
      })
      .catch((error) => {
        if (!mounted) return;
        if (error.response?.status === 402) {
          setState({ checking: false, allowed: false });
          return;
        }
        setState({ checking: false, allowed: true });
      });

    return () => { mounted = false; };
  }, [token, allowInactiveSubscription, location.pathname]);

  if (!token) return <Navigate to="/login" replace />;
  if (allowInactiveSubscription) return children;
  if (!state.allowed) return <Navigate to="/billing" replace state={{ from: location.pathname }} />;
  return children;
}

function RouteFallback() {
  return (
    <div className="route-fallback" role="status" aria-live="polite">
      <span className="sr-only">جاري تحميل الصفحة</span>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/"              element={<Landing />} />
        <Route path="/privacy"       element={<LegalPage type="privacy" />} />
        <Route path="/terms"         element={<LegalPage type="terms" />} />
        <Route path="/refund"        element={<LegalPage type="refund" />} />
        <Route path="/login"         element={<Login />} />
        <Route path="/register"      element={<Register />} />
        <Route path="/scan/:slug"    element={<CustomerPortal />} />
        <Route path="/cashier/*"     element={<Private><Cashier /></Private>} />
        <Route path="/dashboard"     element={<Private><Dashboard /></Private>} />
        <Route path="/cards"         element={<Private><Cards /></Private>} />
        <Route path="/cards/builder" element={<Private><CardBuilder /></Private>} />
        <Route path="/customers"     element={<Private><Customers /></Private>} />
        <Route path="/campaigns"     element={<Private><Campaigns /></Private>} />
        <Route path="/ai"            element={<Private><AIEngine /></Private>} />
        <Route path="/reports"       element={<Private><Reports /></Private>} />
        <Route path="/settings"      element={<Private><Settings /></Private>} />
        <Route path="/billing"       element={<Private allowInactiveSubscription><Billing /></Private>} />
        <Route path="/employees"     element={<Private><Employees /></Private>} />
        <Route path="/branches"      element={<Private><Branches /></Private>} />
        <Route path="/admin"         element={<AdminDashboard />} />
        <Route path="*"              element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
